import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import {
  SkywardAccount,
  SkywardBillCode,
  SkywardConnection,
  SkywardPaymentPlan,
  SkywardSite,
} from '../types/skyward-types'

export type SkywardState = {
  connectInfo?: SkywardConnection
  site?: SkywardSite
  sessionId?: string
  selectedAccount?: SkywardAccount
  accounts: SkywardAccount[]
  billcodes: SkywardBillCode[]
  paymentPlans: SkywardPaymentPlan[]
  error?: string
  loadingStates: {
    connect: boolean
    accounts: boolean
    'low-balance-alerts': boolean
    'create-low-balance-alert': boolean
    'cancel-low-balance-alert': boolean
    'recurring-payments': boolean
    'create-recurring-payment': boolean
    'cancel-recurring-payment': boolean
  }
}

export type SkywardActions = {
  // Cart key management (from context store)
  reset: () => void
  setConnection: (connection: SkywardConnection) => void
  setPaymentPlans: (plans: SkywardPaymentPlan[]) => void
  setSelectedAccount: (id: string) => void

  clearSelectedAccount: () => void
  setSite: (site: SkywardSite) => void
  setAccounts: (accounts: SkywardAccount[]) => void
  setSessionId: (id: string) => void
  setError: (error: string) => void

  setLoadingState: (key: keyof SkywardState['loadingStates'], loading: boolean) => void
}

export type SkywardStore = SkywardState & SkywardActions

const initialState: SkywardState = {
  connectInfo: undefined,
  site: undefined,
  sessionId: undefined,
  selectedAccount: undefined,
  accounts: [],
  billcodes: [],
  paymentPlans: [],
  error: undefined,
  loadingStates: {
    connect: false,
    accounts: false,
    'low-balance-alerts': false,
    'create-low-balance-alert': false,
    'cancel-low-balance-alert': false,
    'create-recurring-payment': false,
    'cancel-recurring-payment': false,
    'recurring-payments': false,
  },
}

export const useSkywardStore = create<SkywardStore>()(
  persist(
    subscribeWithSelector(
      immer((set, _get) => ({
        ...initialState,
        reset: () => {
          set((state) => {
            Object.assign(state, { ...initialState })
          })
        },
        setConnection: (connection: SkywardConnection) => {
          set((state) => {
            state.connectInfo = connection
          })
        },
        setPaymentPlans: (plans: SkywardPaymentPlan[]) => {
          set((state) => {
            state.paymentPlans = plans
          })
        },
        setSessionId: (id: string) => {
          set((state) => {
            state.sessionId = id
          })
        },
        setSelectedAccount: (id: string) => {
          set((state) => {
            const account = state.accounts.find((a) => a.nameId === id)
            if (account?.nameId) {
              state.selectedAccount = account
            }
          })
        },
        setSite: (site: SkywardSite) => {
          set((state) => {
            state.site = site
          })
          // Store SchoolCorpId in localStorage for legacy cart integration
          if (site?.tenantId && typeof localStorage !== 'undefined') {
            localStorage.setItem('schoolCorpId', site.tenantId)
          }
        },
        clearSelectedAccount: () => {
          set((state) => {
            state.selectedAccount = undefined
          })
        },
        setAccounts: (accounts: SkywardAccount[]) => {
          set((state) => {
            state.accounts = accounts
          })
        },
        setError: (error: string) => {
          set((state) => {
            state.error = error
          })
        },
        setLoadingState: (key: keyof SkywardState['loadingStates'], loading: boolean) => {
          set((state) => {
            state.loadingStates[key] = loading
          })
        },
      })),
    ),
    { name: 'skyward-store' },
  ),
)
